/*
 * indexx.ai/about — a new page, not a reskin of the old one.
 *
 * ── Why not edit the existing About ────────────────────────────────────────
 *
 * There already is one, at /indexx-exchange/about. It stays exactly where it
 * is: it is linked from the old footer on 56 pages and from the old header's
 * megamenu, and it is the Exchange's about page by URL. This is indexx.ai's,
 * which is a different claim — the estate rather than one product in it — and
 * it lives at the address the design names.
 *
 * ── The portraits ─────────────────────────────────────────────────────────
 *
 * The approved board drops them and lists names only. They are back here
 * because they were asked for, and they are NOT the old page's assets: those
 * six files are 797x797 rasters wrapped in SVG, 3.1MB across six, and four of
 * them carry the wrong alt text (three different people are announced as
 * "BRIAN"). They have been re-exported to src/assets/team/ at 280px — 464KB
 * for all six — and named after the person they show, so the import itself
 * now says who is in the picture and the alt text cannot drift from it again.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LandingFooter from "./LandingFooter";
import { fetchProducts, ZodiacProduct } from "../../data/zodiacProducts";
import brianZheng from "../../assets/team/brian-zheng.png";
import omkarSunku from "../../assets/team/omkar-sunku.png";
import frankHettmann from "../../assets/team/frank-hettmann.png";
import nickFraser from "../../assets/team/nick-fraser.png";
import tonyPelayo from "../../assets/team/tony-pelayo.png";
import lili from "../../assets/team/lili.png";
import s from "./landing.module.css";

/*
 * Order is the design's, which is seniority, not alphabetical.
 *
 * The estate's nav convention is alphabetical — Brian's standard — but that
 * governs product menus, where alphabetical is a lookup aid. A leadership list
 * is not a lookup; reordering it would put the CEO fourth.
 */
const TEAM: { name: string; role: string; photo: string }[] = [
  { name: "Brian Zheng", role: "President & CEO", photo: brianZheng },
  { name: "Omkar Sunku", role: "Chief Technology Officer", photo: omkarSunku },
  { name: "Frank Hettmann", role: "Chief Compliance Officer", photo: frankHettmann },
  { name: "Nick Fraser", role: "Chief Operations Officer", photo: nickFraser },
  { name: "Tony Pelayo", role: "Chief Marketing & Sales Officer", photo: tonyPelayo },
  { name: "Lili", role: "Chief Financial Officer", photo: lili },
];

const COUNT_WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven"];

export default function About() {
  const [products, setProducts] = useState<ZodiacProduct[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    fetchProducts(ac.signal).then(setProducts);
    return () => ac.abort();
  }, []);


  /*
   * The design prints 16. This counts instead.
   *
   * Identical reasoning to CountsStrip: a number typed next to a list it
   * describes is a claim that goes stale the moment a seventeenth product
   * ships from the registry. Until the count is known it is an em dash, which
   * is this estate's own convention for a figure with no source — never a 0,
   * which would read as a real and very wrong answer.
   */
  const productCount = products.length > 0 ? String(products.length) : "—";

  return (
    <div className={s.root}>
      <main className={`${s.shell} ${s.aboutMain}`}>
        <div className={s.sectionHeadText}>
          <p className={s.eyebrow}>About</p>
          <h1 className={`${s.h1About} ${s.display}`}>
            We empower you to do more with your money.
          </h1>
          <p className={s.aboutLede}>
            The new economy has arrived. We believe America should lead in crypto and
            we&rsquo;re committed to helping people across the U.S. access the world of
            digital assets.
          </p>
        </div>

        <div className={s.stats}>
          <div className={s.stat}>
            <span className={s.statLabel}>Launched September</span>
            <span className={`${s.statValue} ${s.display}`}>2019</span>
          </div>
          <div className={s.stat}>
            <span className={s.statLabel}>Headquartered in</span>
            <span className={`${s.statValue} ${s.display}`}>California</span>
          </div>
          <div className={s.stat}>
            <span className={s.statLabel}>Products</span>
            <span className={`${s.statValue} ${s.display}`}>{productCount}</span>
          </div>
          <div className={s.stat}>
            <span className={s.statLabel}>Account</span>
            <span className={`${s.statValue} ${s.display}`}>1</span>
          </div>
        </div>

        <section className={s.split}>
          <h2 className={`${s.splitHead} ${s.display}`}>
            Buy. Trade. Earn.
            <br />
            Your Way.
          </h2>
          <div className={s.splitBody}>
            <p>
              Markets are always on the move, but peace of mind is priceless. indexx.ai
              provides secure and reliable access to the world&rsquo;s most popular
              cryptocurrencies, with some of the lowest fees in the industry.
            </p>
            <p>
              Launched in September 2019 and headquartered in California, indexx.ai was
              established to serve U.S. consumers and adhere to U.S. regulations.
            </p>
          </div>
        </section>

        <section>
          <div className={s.teamHead}>
            <h2 className={`${s.h2About} ${s.display}`}>Meet our executive team</h2>
            <span className={s.teamCount}>{COUNT_WORDS[TEAM.length] || TEAM.length}</span>
          </div>

          <div className={s.team}>
            {TEAM.map((person) => (
              <div className={s.person} key={person.name}>
                {/*
                  alt is the name, not "portrait of" or "headshot".

                  A screen reader already announces this as an image; prefixing
                  the role would read it twice, since the role is right there in
                  the text beside it.
                */}
                <img className={s.portrait} src={person.photo} alt={person.name} />
                <span className={s.personText}>
                  <span className={`${s.personName} ${s.display}`}>{person.name}</span>
                  <span className={s.personRole}>{person.role}</span>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className={s.careers}>
          <p className={`${s.careersLine} ${s.display}`}>
            Make a career out of crypto. Join the indexx.ai team and help us take the
            future of money to new heights.
          </p>
          {/* The design points at #careers. This route already exists. */}
          <Link className={s.ctaPrimary} to="/indexx-exchange/careers">
            See open roles
          </Link>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
